import { List, OrderedMap, Record } from 'immutable';
import { Utils, C } from '@kineticdata/bundle-common';
const { noPayload, withPayload } = Utils;
const ns = Utils.namespaceBuilder('services/settings/services');

export const types = {
  FETCH_SERVICES_REQUEST: ns('FETCH_SERVICES_REQUEST'),
  FETCH_SERVICES_SUCCESS: ns('FETCH_SERVICES_SUCCESS'),
  FETCH_SERVICES_FAILURE: ns('FETCH_SERVICES_FAILURE'),

  FETCH_SERVICE_REQUEST: ns('FETCH_SERVICE_REQUEST'),
  FETCH_SERVICE_SUCCESS: ns('FETCH_SERVICE_SUCCESS'),
  FETCH_SERVICE_FAILURE: ns('FETCH_SERVICE_FAILURE'),

  FETCH_SERVICE_REVISION_REQUEST: ns('FETCH_SERVICE_REVISION_REQUEST'),
  FETCH_SERVICE_REVISION_SUCCESS: ns('FETCH_SERVICE_REVISION_SUCCESS'),
  FETCH_SERVICE_REVISION_FAILURE: ns('FETCH_SERVICE_REVISION_FAILURE'),
  RESET_SERVICE_REVISION_REQUEST: ns('RESET_SERVICE_REVISION_REQUEST'),

  CREATE_SERVICE_REQUEST: ns('CREATE_SERVICE_REQUEST'),
  CLONE_SERVICE_REQUEST: ns('CLONE_SERVICE_REQUEST'),
  EXPIRE_SERVICE_REQUEST: ns('EXPIRE_SERVICE_REQUEST'),
  SAVE_SERVICE_REVISION_REQUEST: ns('SAVE_SERVICE_REVISION_REQUEST'),
  SUBMIT_SERVICE_REVISION_REQUEST: ns('SUBMIT_SERVICE_REVISION_REQUEST'),
  DELETE_SERVICE_REVISION_REQUEST: ns('DELETE_SERVICE_REVISION_REQUEST'),
  LOCK_SERVICE_REVISION_REQUEST: ns('LOCK_SERVICE_REVISION_REQUEST'),

  FETCH_CATEGORIES_REQUEST: ns('FETCH_CATEGORIES_REQUEST'),
  FETCH_CATEGORIES_COMPLETE: ns('FETCH_CATEGORIES_COMPLETE'),
  SYNC_CATEGORIES_REQUEST: ns('SYNC_CATEGORIES_REQUEST'),

  SERVICE_ACTION_COMPLETE: ns('SERVICE_ACTION_COMPLETE'),
};

export const actions = {
  fetchServicesRequest: noPayload(types.FETCH_SERVICES_REQUEST),
  fetchServicesSuccess: withPayload(types.FETCH_SERVICES_SUCCESS),
  fetchServicesFailure: withPayload(types.FETCH_SERVICES_FAILURE),

  fetchServiceRequest: withPayload(types.FETCH_SERVICE_REQUEST),
  fetchServiceSuccess: withPayload(types.FETCH_SERVICE_SUCCESS),
  fetchServiceFailure: withPayload(types.FETCH_SERVICE_FAILURE),

  fetchServiceRevisionRequest: withPayload(
    types.FETCH_SERVICE_REVISION_REQUEST,
    'revisionId',
    'serviceId',
  ),
  fetchServiceRevisionSuccess: withPayload(
    types.FETCH_SERVICE_REVISION_SUCCESS,
  ),
  fetchServiceRevisionFailure: withPayload(
    types.FETCH_SERVICE_REVISION_FAILURE,
  ),
  resetServiceRevisionRequest: withPayload(
    types.RESET_SERVICE_REVISION_REQUEST,
  ),

  createServiceRequest: withPayload(types.CREATE_SERVICE_REQUEST),
  cloneServiceRequest: withPayload(types.CLONE_SERVICE_REQUEST),
  expireServiceRequest: withPayload(types.EXPIRE_SERVICE_REQUEST),
  saveServiceRevisionRequest: withPayload(types.SAVE_SERVICE_REVISION_REQUEST),
  submitServiceRevisionRequest: withPayload(
    types.SUBMIT_SERVICE_REVISION_REQUEST,
  ),
  deleteServiceRevisionRequest: withPayload(
    types.DELETE_SERVICE_REVISION_REQUEST,
  ),
  lockServiceRevisionRequest: withPayload(types.LOCK_SERVICE_REVISION_REQUEST),

  fetchCategoriesRequest: withPayload(types.FETCH_CATEGORIES_REQUEST),
  fetchCategoriesComplete: withPayload(types.FETCH_CATEGORIES_COMPLETE),
  syncCategoriesRequest: withPayload(types.SYNC_CATEGORIES_REQUEST),

  serviceActionComplete: withPayload(types.SERVICE_ACTION_COMPLETE),
};

export const State = Record({
  data: OrderedMap(),
  dataList: List(),
  dataInit: false,
  dataLoading: false,
  dataError: null,

  current: null,
  currentLoading: false,
  currentError: null,

  revision: null,
  revisionLoading: false,
  revisionError: null,

  categories: null,
  categoriesSyncAvailable: false,

  actionsPending: 0,
});

const updateDataList = state =>
  state.set(
    'dataList',
    state.data
      .map(l => l.find(p => p.values['Status'] === 'Active') || l.first())
      .toList(),
  );

export const reducer = (state = State(), { type, payload }) => {
  switch (type) {
    case types.FETCH_SERVICES_REQUEST:
      return state.set('dataError', null).set('dataLoading', true);
    case types.FETCH_SERVICES_SUCCESS:
      return (
        state
          // Group services by Service Id, sort each group by updatedAt:DESC,
          // and sort the map alphabetically.
          // TODO allow custom sort order
          .set(
            'data',
            List(payload)
              .groupBy(p => p.values[C.FIELD_SERVICE_ID])
              .toOrderedMap()
              .map(l => l.sortBy(p => p.updatedAt).reverse())
              .sortBy(l => l.first().values['Name'].toLowerCase()),
          )
          .set('dataInit', true)
          .set('dataLoading', false)
          // Create a list of all current services
          .update(updateDataList)
      );
    case types.FETCH_SERVICES_FAILURE:
      return state.set('dataError', payload).set('dataLoading', false);

    case types.FETCH_SERVICE_REQUEST:
      return state
        .update(
          'current',
          current =>
            // If serviceId changed, set new current service object
            !current || current.values[C.FIELD_SERVICE_ID] !== payload
              ? state.dataList.find(
                  p => p.values[C.FIELD_SERVICE_ID] === payload,
                )
              : current,
        )
        .set('currentError', null)
        .set('currentLoading', true);
    case types.FETCH_SERVICE_SUCCESS:
      return (
        state
          .set('current', payload.record)
          .set('currentLoading', false)
          // Update the list of protfolios for the current id
          .update('data', data =>
            data.set(
              payload.serviceId,
              List(payload.list)
                .sortBy(p => p.updatedAt)
                .reverse(),
            ),
          )
          // Update list of all current services with latest data
          .update(updateDataList)
      );
    case types.FETCH_SERVICE_FAILURE:
      return state.set('currentError', payload).set('currentLoading', false);

    case types.FETCH_SERVICE_REVISION_REQUEST:
      return state
        .update(
          'revision',
          p =>
            // If revisionId changed, clear revision service object
            !p || p.id !== payload.revisionId ? null : p,
        )
        .set('revisionError', null)
        .set('revisionLoading', true);
    case types.FETCH_SERVICE_REVISION_SUCCESS:
      return (
        state
          .set('revision', payload)
          .set('revisionLoading', false)
          // Update the revision record in the list of services
          .updateIn(
            ['data', payload.values[C.FIELD_SERVICE_ID]],
            list =>
              list
                ? list
                    .map(
                      p =>
                        p.id === payload.id
                          ? // Modify the revision to only have the necessary values
                            // when adding to data list to prevent the redux store
                            // from having a ton of data due to all the Content.
                            {
                              ...payload,
                              values: C.SERVICE_PRIMARY_FIELDS.reduce(
                                (vals, field) => ({
                                  ...vals,
                                  [field]: payload.values[field],
                                }),
                                {},
                              ),
                            }
                          : p,
                    )
                    .sortBy(p => p.updatedAt)
                    .reverse()
                : list,
          )
          // Update list of all current services with latest data
          .update(updateDataList)
      );
    case types.FETCH_SERVICE_REVISION_FAILURE:
      return state.set('revisionError', payload).set('revisionLoading', false);
    case types.RESET_SERVICE_REVISION_REQUEST:
      // If the current revision matches the one we wantt o reset, clear revision state
      return state.getIn(['revision', 'id']) === payload
        ? state
            .set('revision', null)
            .set('revisionLoading', false)
            .set('revisionError', null)
        : state;

    case types.FETCH_CATEGORIES_REQUEST:
      return state.set('categoriesSyncAvailable', false);
    case types.FETCH_CATEGORIES_COMPLETE:
      return state
        .set('categories', List(payload))
        .set('categoriesSyncAvailable', true);
    case types.SYNC_CATEGORIES_REQUEST:
      return state.set('categoriesSyncAvailable', false);

    case types.CREATE_SERVICE_REQUEST:
    case types.CLONE_SERVICE_REQUEST:
    case types.EXPIRE_SERVICE_REQUEST:
    case types.SAVE_SERVICE_REVISION_REQUEST:
    case types.SUBMIT_SERVICE_REVISION_REQUEST:
    case types.DELETE_SERVICE_REVISION_REQUEST:
    case types.LOCK_SERVICE_REVISION_REQUEST:
      return state.update('actionsPending', a => a + 1);
    case types.SERVICE_ACTION_COMPLETE:
      return state.update('actionsPending', a => a - 1);

    default:
      return state;
  }
};

export default reducer;

export const selectIsServiceOwner = (profile, service) =>
  Utils.isMemberOf(profile || {}, C.SMO_TEAM_NAME) ||
  (!!service &&
    (service.values['Technical Owners'] || []).some(t =>
      Utils.isMemberOf(profile || {}, t),
    ));
